import React from 'react';
import { formatHourLabel, formatMinutes, minutesToFraction } from './timeUtils';

/**
 * Hour/half-hour tick marks along the top of the timeline viewport.
 *
 * Tick density adapts to the window span:
 *   ≤ 3h span  → every 15 min
 *   ≤ 8h span  → every 30 min
 *   > 8h span  → every hour
 *
 * All positioning is percentage-based so the axis auto-fits its flex parent —
 * no ResizeObserver round-trip required.
 */

const pickInterval = (spanMin) => {
  if (spanMin <= 180) return 15;
  if (spanMin <= 480) return 30;
  return 60;
};

// For the full-day view we only have ~25px per hour on a typical viewport,
// so drawing a label at every hour produces an unreadable smear. Step up to
// every 3 hours once the span crosses 12 hours.
const labelStride = (spanMin) => {
  if (spanMin > 12 * 60) return 3;
  return 1;
};

export const AXIS_HEIGHT = 28;

// Multi-day axis renders day columns with the date label instead of hour
// ticks — the whole point of the week/month zoom is "which day" not "which
// hour". Called when `win.windowDays > 1` is set by the parent.
const MultiDayAxis = ({ window: win }) => {
  const { startMin, endMin, windowDays, windowStartISO } = win;
  const span = Math.max(1, endMin - startMin);
  const todayISO = new Date().toISOString().slice(0, 10);
  const days = [];
  for (let i = 0; i < windowDays; i++) {
    const anchor = windowStartISO ? new Date(`${windowStartISO}T00:00:00`) : new Date();
    anchor.setDate(anchor.getDate() + i);
    const iso = anchor.toISOString().slice(0, 10);
    const dayStartMin = i * 1440;
    days.push({
      i,
      iso,
      dow: anchor.getDay(),
      day: anchor.getDate(),
      monthShort: anchor.toLocaleDateString('en-US', { month: 'short' }),
      leftPct: ((dayStartMin - startMin) / span) * 100,
      widthPct: (1440 / span) * 100,
      isToday: iso === todayISO,
      isWeekend: anchor.getDay() === 0 || anchor.getDay() === 6,
    });
  }
  return (
    <div style={{
      position: 'relative', height: `${AXIS_HEIGHT}px`,
      borderBottom: '1px solid var(--border-subtle, #ececea)',
      backgroundColor: 'var(--surface-sunken, #fafaf9)',
    }}>
      {days.map((d) => (
        <div
          key={d.iso}
          style={{
            position: 'absolute',
            left: `${d.leftPct}%`,
            width: `${d.widthPct}%`,
            top: 0,
            bottom: 0,
            borderLeft: '1px solid var(--border-default, #e7e5e4)',
            backgroundColor: d.isWeekend ? 'rgba(148,163,184,0.06)' : 'transparent',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            gap: '4px',
            fontSize: '10px',
            fontWeight: 700,
            letterSpacing: '0.02em',
            color: d.isToday ? 'var(--color-brand-600, #a07d29)' : 'var(--text-muted)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {windowDays <= 7 ? (
            <>{['Sun','Mon','Tue','Wed','Thu','Fri','Sat'][d.dow]} · {d.day} {d.monthShort}</>
          ) : (
            <>{d.day}{d.day === 1 || d.isToday ? ` ${d.monthShort}` : ''}</>
          )}
        </div>
      ))}
    </div>
  );
};

const TimelineAxis = ({ window: win }) => {
  if (win.windowDays && win.windowDays > 1) return <MultiDayAxis window={win} />;
  const { startMin, endMin } = win;
  const spanMin = endMin - startMin;
  const interval = pickInterval(spanMin);

  const firstTick = Math.ceil(startMin / interval) * interval;
  const ticks = [];
  for (let t = firstTick; t <= endMin; t += interval) ticks.push(t);
  const stride = labelStride(spanMin);

  return (
    <div style={{
      position: 'relative', height: `${AXIS_HEIGHT}px`,
      borderBottom: '1px solid var(--border-subtle, #ececea)',
      backgroundColor: 'var(--surface-sunken, #fafaf9)',
    }}>
      {ticks.map((t) => {
        const frac = minutesToFraction(t, win);
        const isHour = t % 60 === 0;
        const hour = Math.floor(t / 60);
        return (
          <div
            key={t}
            style={{
              position: 'absolute',
              left: `${frac * 100}%`,
              top: 0,
              bottom: 0,
              borderLeft: `1px solid ${isHour ? 'var(--border-default, #e7e5e4)' : 'var(--border-subtle, #ececea)'}`,
            }}
          >
            {isHour ? (
              hour % stride === 0 && (
                <div style={{
                  position: 'absolute', top: '6px', left: '6px',
                  fontSize: '10px', fontWeight: 700, color: 'var(--text-muted)',
                  whiteSpace: 'nowrap', letterSpacing: '0.02em',
                }}>
                  {formatHourLabel(hour)}
                </div>
              )
            ) : (
              interval <= 15 && (
                <div style={{
                  position: 'absolute', top: '6px', left: '4px',
                  fontSize: '9px', fontWeight: 600, color: 'var(--text-subtle)',
                  whiteSpace: 'nowrap',
                }}>
                  {formatMinutes(t)}
                </div>
              )
            )}
          </div>
        );
      })}
    </div>
  );
};

/**
 * Vertical NOW line + attached pill label. Percentage-positioned inside the
 * tracks container so it follows the viewport resize without needing a
 * measured width.
 *
 * z-index: above tracks (5), below tooltip (100).
 */
export const NowIndicator = ({ window: win, totalHeight, nowMin, currentLabel }) => {
  const { startMin, endMin, windowDays, windowStartISO } = win;
  // Multi-day mode: caller still passes minutes-of-day; convert to
  // minutes-in-window by inserting the offset for today vs windowStartISO.
  // Skip entirely if today is outside the visible range.
  let effectiveNow = nowMin;
  if (windowDays && windowDays > 1 && windowStartISO) {
    const todayISO = new Date().toISOString().slice(0, 10);
    const start = new Date(`${windowStartISO}T00:00:00`);
    const today = new Date(`${todayISO}T00:00:00`);
    const offsetDays = Math.round((today - start) / 86400000);
    if (offsetDays < 0 || offsetDays >= windowDays) return null;
    effectiveNow = offsetDays * 1440 + nowMin;
  }
  if (effectiveNow < startMin || effectiveNow > endMin) return null;

  const leftPct = minutesToFraction(effectiveNow, { startMin, endMin }) * 100;
  return (
    <>
      {/* Subtle NOW line: thin, semi-transparent red so it reads as a
          marker without competing with slot content for attention. */}
      <div style={{
        position: 'absolute',
        left: `${leftPct}%`,
        top: 0,
        height: `${totalHeight}px`,
        width: '1px',
        backgroundColor: 'rgba(239, 68, 68, 0.55)',
        zIndex: 5,
        pointerEvents: 'none',
      }} />
      {/* Time chip sits in the axis margin, outlined not filled, so it reads
          as a subtle tick rather than a red stop sign. Content name lives in
          the title tooltip. */}
      <div
        title={currentLabel ? `Now airing: ${currentLabel}` : undefined}
        style={{
          position: 'absolute',
          left: `${leftPct}%`,
          top: '-14px',
          transform: 'translateX(-50%)',
          backgroundColor: '#fff',
          color: '#ef4444',
          padding: '1px 7px',
          borderRadius: '999px',
          fontSize: '10px',
          fontWeight: 600,
          letterSpacing: '0.02em',
          zIndex: 6,
          pointerEvents: 'auto',
          border: '1px solid rgba(239, 68, 68, 0.55)',
          whiteSpace: 'nowrap',
          lineHeight: 1.2,
        }}
      >
        {windowDays && windowDays > 1 ? 'Now' : formatMinutes(nowMin)}
      </div>
    </>
  );
};

export default TimelineAxis;
